"use client"

/**
 * Sticky Comparison Bar Component
 *
 * The bottom tray on product and collection pages. It shows the SAME compare
 * list the header's Compare button and panel show, because both now read and
 * write through components/homepage-2026/saved-compare-store.ts.
 *
 * FRIDGE-873 — why the store, and not localStorage directly. This file used to
 * own `fridge_comparison_products` itself: it kept the list in React state,
 * wrote the key with its own setItem helpers, and told nobody. The header's
 * badge subscribes to the browser `storage` event, which is delivered to OTHER
 * tabs only and never to the document that made the write — so ticking Compare
 * on a product or collection page left the header badge stale until a reload,
 * which is exactly the "compare in header does not work" report. Reading the
 * list from the store (a `useSyncExternalStore` subscription) makes this bar and
 * the header two views of one list, in both directions and with no reload.
 *
 * The store also maintains the `_meta` sidecar that carries each row's brand and
 * (FIN) Fridge.com Identification Number, which the legacy row shape has no slot
 * for — so removing a row here no longer orphans its metadata.
 */

import { useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
// FRIDGE-1112 — brand the tray through Button VARIANTS, never a className
// colour override. `cn` is clsx only (lib/utils.ts deliberately drops
// tailwind-merge), so an override and the variant's own `bg-primary` both land
// on the element at equal specificity and compiled-CSS order decides. Tailwind
// emits arbitrary hex utilities first, then named colours alphabetically, so
// `bg-primary` beats every `bg-[#hex]` and anything sorting before "primary" —
// which is why `bg-blue-600` here rendered near-black for as long as it shipped.
import Image from 'next/image'
import Link from 'next/link'
import {
  useSavedCompare,
  addCompareItem,
  compareHref,
  COMPARE_MAX,
  type SavedItem,
} from '@/components/homepage-2026/saved-compare-store'

interface ComparisonProduct {
  id: string
  title: string
  image: string
  canonical_url: string
  price?: number
}

const MAX_PRODUCTS = COMPARE_MAX

export function ComparisonBar() {
  const sc = useSavedCompare()
  const products = sc.compare

  useEffect(() => {
    // Listen for custom events when products are added from elsewhere on the page.
    const handleAdd = (e: Event) => {
      const detail = (e as CustomEvent<ComparisonProduct>).detail
      if (!detail?.id && !detail?.canonical_url) return
      addCompareItem(toSavedItem(detail))
    }
    window.addEventListener('comparison:add', handleAdd)
    return () => window.removeEventListener('comparison:add', handleAdd)
  }, [])

  if (products.length === 0) return null

  return (
    <div data-testid="comparison-bar" className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#0469f0] shadow-2xl z-50 animate-in slide-in-from-bottom">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm sm:text-base">
              Compare Products ({products.length}/{MAX_PRODUCTS})
            </h3>
          </div>

          <div className="flex items-center gap-2 flex-1 overflow-x-auto">
            {products.map(product => (
              <div key={product.id || product.href} className="relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 border rounded-lg overflow-hidden bg-white">
                {/* A row can now arrive from a surface that has no photo (a deals
                    card whose image failed to resolve, an article-sidebar card
                    rendered from `images: []`). next/image THROWS on an empty
                    `src`, which would take the whole page down rather than lose a
                    thumbnail, so an imageless row gets the initial instead. */}
                {product.image ? (
                  <Image
                    src={product.image}
                    alt={product.title ? `${product.title} | Fridge.com` : ''}
                    fill
                    className="object-cover"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center justify-center bg-gray-50 text-gray-400 text-lg font-bold"
                  >
                    {(product.title || '?').trim().charAt(0).toUpperCase()}
                  </span>
                )}
                <button
                  onClick={() => sc.removeCompare({ id: product.id, href: product.href })}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                  aria-label="Remove from comparison"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => sc.clearCompare()}
              className="hidden sm:inline-flex text-[#0469f0] border-[#0469f0]"
            >
              Clear All
            </Button>
            {/* FRIDGE-873 — "Compare Now" was a bare <Button> with no onClick and
                no href: the tray filled up and the button did nothing, at every
                width, for as long as it has shipped. It now opens the compare
                tool on exactly these products (`/compare?products=<slug,slug>`),
                the same URL the header's Compare panel links to. Under two
                products there is nothing to compare, so the disabled BUTTON is
                kept rather than a dead link — a disabled <a> is not a thing. */}
            {products.length >= 2 ? (
              <Button asChild size="sm" variant="ctaPrimary">
                <Link href={compareHref(products)}>Compare Now</Link>
              </Button>
            ) : (
              <Button size="sm" variant="ctaPrimary" disabled>
                Compare Now
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function toSavedItem(p: ComparisonProduct): SavedItem {
  return {
    id: p.id,
    fin: '',
    brand: '',
    title: p.title,
    image: p.image,
    href: p.canonical_url,
    priceCents: typeof p.price === 'number' ? Math.round(p.price * 100) : 0,
  }
}

/**
 * Helper function to add product to comparison from anywhere.
 * Kept as the page-level event so existing callers do not change; the handler
 * above routes it into the shared store, so the header badge updates instantly.
 */
export function addToComparison(product: ComparisonProduct) {
  const event = new CustomEvent('comparison:add', { detail: product })
  window.dispatchEvent(event)
}
